import { useEffect } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import { Home, ClipboardList, PlusCircle, UserCircle2, LayoutDashboard, FileText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BrandMark } from "@/components/Brand";
import { NotificationBell } from "@/components/NotificationBell";
import { api, getPosition } from "@/lib/api";

const TABS = [
  { to: "/app", end: true, label: "Home", icon: Home, id: "home" },
  { to: "/app/tasks", label: "My Tasks", icon: ClipboardList, id: "tasks" },
  { to: "/app/lead", label: "Add Lead", icon: PlusCircle, id: "lead" },
  { to: "/app/quotes", label: "Quotes", icon: FileText, id: "quotes" },
  { to: "/app/profile", label: "Profile", icon: UserCircle2, id: "profile" },
];

const useLocationPings = () => {
  useEffect(() => {
    const tick = async () => {
      try {
        const { data } = await api.get("/attendance/today");
        const r = data.record;
        if (!r?.clock_in_time || r.clock_out_time) return;
        const pos = await getPosition();
        await api.post("/location/ping", pos);
      } catch {
        /* location unavailable */
      }
    };
    tick();
    const t = setInterval(tick, 60000);
    return () => clearInterval(t);
  }, []);
};

export default function StaffLayout() {
  const { user } = useAuth();
  useLocationPings();
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-4 h-16 flex items-center justify-between">
          <BrandMark />
          <div className="flex items-center gap-1">
            {user.role !== "staff" && (
              <Link to="/admin" data-testid="staff-to-admin-link" className="h-10 w-10 grid place-items-center rounded-full hover:bg-slate-100 text-slate-600">
                <LayoutDashboard className="h-5 w-5" />
              </Link>
            )}
            <NotificationBell />
          </div>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-4 pt-5 pb-28">
        <Outlet />
      </main>
      <nav className="fixed bottom-0 inset-x-0 z-30 bg-white border-t border-slate-200 shadow-[0_-4px_20px_rgba(15,23,42,0.06)]" data-testid="staff-bottom-nav">
        <div className="max-w-2xl mx-auto grid grid-cols-5 h-16">
          {TABS.map(({ to, end, label, icon: Icon, id }) => (
            <NavLink key={to} to={to} end={end} data-testid={`bottom-nav-${id}`}
              className={({ isActive }) => `flex flex-col items-center justify-center gap-1 text-[11px] font-semibold transition-colors ${isActive ? "text-sky-700" : "text-slate-500 hover:text-slate-800"}`}>
              {({ isActive }) => (
                <>
                  <span className={`px-3 py-1 rounded-full transition-colors ${isActive ? "bg-sky-100" : ""}`}>
                    <Icon className="h-5 w-5" />
                  </span>
                  {label}
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
